import { useTranslation } from "@redotech/redo-i18n";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import MoonIcon from "@redotech/redo-icons/moon-01_filled.svg";
import SunIcon from "@redotech/redo-icons/sun.svg";
import { useAiSlidePanel } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import classNames from "classnames";
import { Fragment, memo, useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { getProductLabel, getProductStepLabel } from "../../product-labels";
import type { ProductEntry } from "../../products";
import { AiStarIcon } from "../ui/ai-star-icon";
import { ExitOnboardingButton } from "../ui/exit-onboarding-button";
import * as styles from "./onboarding-header.module.css";
import { deriveOnboardingProgress } from "./onboarding-progress";
import { SetupLocaleSelector } from "./setup-locale-selector";

interface ProductHeaderProps {
  logoUrl?: string;
  product: ProductEntry;
  showProgress?: boolean;
  storeName?: string;
}

export const ProductHeader = memo(function ProductHeader({
  product,
  storeName: storeNameProp,
  logoUrl,
  showProgress = true,
}: ProductHeaderProps) {
  const { t } = useTranslation();
  const storeName = storeNameProp ?? t("My Store");
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(false);

  const toggleDarkMode = useCallback(() => {
    setIsDarkMode((prev) => {
      const next = !prev;
      document.documentElement.setAttribute(
        "data-theme",
        next ? "dark" : "light",
      );
      return next;
    });
  }, []);

  const initials = useMemo(() => {
    const words = storeName.trim().split(/\s+/);
    if (words.length >= 2) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return (words[0]![0]! + words[1]![0]!).toUpperCase();
    }
    return (words[0]?.[0] ?? "").toUpperCase();
  }, [storeName]);

  const currentPath = location.pathname.split("/").pop();
  const progress = useMemo(
    () => deriveOnboardingProgress(product.steps, currentPath),
    [currentPath, product.steps],
  );
  const showAskRedo =
    product.id === "returns" || product.id === "order-editing";
  const { toggle } = useAiSlidePanel();
  const toggleAskRedo = useCallback(() => toggle(), [toggle]);

  return (
    <header className={styles.header}>
      <div className={styles.navBar}>
        <div className={styles.brandGroup}>
          <div className={styles.profileAvatar}>
            {logoUrl ? (
              <img
                alt={storeName}
                className={styles.profileImage}
                src={logoUrl}
              />
            ) : (
              <span className={styles.profileInitials}>{initials}</span>
            )}
          </div>
          <span className={styles.storeName}>{storeName}</span>
          <div className={styles.productTag}>
            {product.icon && (
              <product.icon className={styles.productIcon} size={14} />
            )}
            <span className={styles.productName}>
              {getProductLabel(product)}
            </span>
          </div>
        </div>

        {showProgress && progress.items.length > 1 && (
          <div className={styles.stepLabels}>
            {progress.items.map((item, i) => {
              const reached =
                progress.isComplete || i <= progress.activeItemIndex;
              return (
                <Fragment key={item.id}>
                  {i > 0 && (
                    <RedoIcon
                      className={classNames(styles.chevron, {
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        [styles.chevronActive!]: reached,
                      })}
                      Icon={ChevronRightIcon}
                    />
                  )}
                  <span
                    className={classNames(styles.stepLabel, {
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      [styles.stepLabelActive!]: reached,
                    })}
                  >
                    {getProductStepLabel(item.label)}
                  </span>
                </Fragment>
              );
            })}
          </div>
        )}

        <div className={styles.rightGroup}>
          <div className={styles.themeTag} onClick={toggleDarkMode}>
            {isDarkMode ? (
              <RedoIcon className={styles.themeIcon} Icon={SunIcon} />
            ) : (
              <RedoIcon className={styles.themeIcon} Icon={MoonIcon} />
            )}
          </div>
          {showAskRedo && (
            <button
              className={styles.askRedoTag}
              onClick={toggleAskRedo}
              type="button"
            >
              <AiStarIcon className={styles.askRedoIcon} />
              <span className={styles.askRedoText}>{t("Ask Redo")}</span>
            </button>
          )}
          <SetupLocaleSelector />
          <ExitOnboardingButton
            className={styles.exitTag}
            textClassName={styles.exitText}
          />
        </div>
      </div>
      {showProgress && progress.visibleStepCount > 1 ? (
        <div className={styles.progressTrack}>
          <div
            className={styles.progressFill}
            style={{
              width: `${
                ((progress.isComplete
                  ? progress.visibleStepCount
                  : progress.visibleStepIndex + 1) /
                  progress.visibleStepCount) *
                100
              }%`,
            }}
          />
        </div>
      ) : (
        <div className={styles.divider} />
      )}
    </header>
  );
});
